/**
 * IndexedDB wrapper for offline data storage
 * Provides persistent storage for visit notes, photos, and other data
 * that need to sync when connection is restored
 */

const DB_NAME = 'asiliflow_offline'
const DB_VERSION = 1

// Store names
export const STORES = {
  PENDING_VISIT_NOTES: 'pending_visit_notes',
  PENDING_PHOTOS: 'pending_photos',
  PENDING_INCIDENTS: 'pending_incidents',
  CACHED_VISITS: 'cached_visits',
  CACHED_CLIENTS: 'cached_clients',
  SYNC_QUEUE: 'sync_queue',
} as const

export type StoreName = (typeof STORES)[keyof typeof STORES]

// Types for pending items
export interface PendingItem {
  id: string
  createdAt: number
  retryCount: number
  lastRetry?: number
  error?: string
}

export interface PendingVisitNote extends PendingItem {
  type: 'visit_note'
  data: {
    visitId: string
    clientId: string
    agencyId: string
    caregiverId: string
    userId: string
    userEmail: string
    userRole: string
    summary: string
    clientCondition: string
    clientMood?: string
    activitiesCompleted?: string
    medicationsAdministered?: string[]
    vitalSigns?: Record<string, unknown>
    tasksCompleted: Array<{
      taskId: string
      description: string
      completed: boolean
      notes?: string
    }>
    photos?: Array<{
      id: string
      type: string
      dataUrl: string
      caption?: string
      timestamp?: Date
    }>
    signature: { dataUrl: string }
    aiGenerated: boolean
    aiTranscript?: string
    // Billing & Administrative
    documentationTime?: number
    travelTime?: number
    timeIn?: string
    timeOut?: string
    hcpcsCode?: string
    locationCode?: string
    billable?: boolean
    clinicianCredentials?: string
    clinicalScales?: Record<string, unknown>
    // Visit-type flags
    missedVisit?: boolean
    missedVisitReason?: string
    prn?: boolean
    external?: boolean
    // Generic attachments
    attachments?: Array<{
      id: string
      url: string
      fileName: string
      contentType: string
      sizeBytes: number
      uploadedAt: Date
    }>
  }
}

export interface SyncQueueItem extends PendingItem {
  storeName: StoreName
  action: 'create' | 'update' | 'delete'
  itemId: string
}

// Open database connection
let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('Failed to open offline database:', request.error)
      reject(request.error)
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.PENDING_VISIT_NOTES)) {
        const visitNoteStore = db.createObjectStore(STORES.PENDING_VISIT_NOTES, { keyPath: 'id' })
        visitNoteStore.createIndex('createdAt', 'createdAt', { unique: false })
        visitNoteStore.createIndex('visitId', 'data.visitId', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.PENDING_PHOTOS)) {
        const photoStore = db.createObjectStore(STORES.PENDING_PHOTOS, { keyPath: 'id' })
        photoStore.createIndex('visitNoteId', 'visitNoteId', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.PENDING_INCIDENTS)) {
        const incidentStore = db.createObjectStore(STORES.PENDING_INCIDENTS, { keyPath: 'id' })
        incidentStore.createIndex('createdAt', 'createdAt', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.CACHED_VISITS)) {
        const cachedVisitsStore = db.createObjectStore(STORES.CACHED_VISITS, { keyPath: 'id' })
        cachedVisitsStore.createIndex('caregiverId', 'caregiverId', { unique: false })
        cachedVisitsStore.createIndex('date', 'date', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORES.CACHED_CLIENTS)) {
        db.createObjectStore(STORES.CACHED_CLIENTS, { keyPath: 'id' })
      }

      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' })
        syncStore.createIndex('createdAt', 'createdAt', { unique: false })
        syncStore.createIndex('storeName', 'storeName', { unique: false })
      }
    }
  })

  return dbPromise
}

// Generic CRUD operations
export async function addItem<T extends PendingItem>(
  storeName: StoreName,
  item: T
): Promise<string> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.add(item)

    request.onsuccess = () => resolve(item.id)
    request.onerror = () => reject(request.error)
  })
}

export async function getItem<T>(
  storeName: StoreName,
  id: string
): Promise<T | undefined> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.get(id)

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function getAllItems<T>(storeName: StoreName): Promise<T[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.getAll()

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function updateItem<T extends PendingItem>(
  storeName: StoreName,
  item: T
): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.put(item)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function deleteItem(
  storeName: StoreName,
  id: string
): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.delete(id)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function clearStore(storeName: StoreName): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.clear()

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// Get count of pending items
export async function getPendingCount(storeName: StoreName): Promise<number> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.count()

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Get items by index
export async function getItemsByIndex<T>(
  storeName: StoreName,
  indexName: string,
  value: IDBValidKey
): Promise<T[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly')
    const store = transaction.objectStore(storeName)
    const index = store.index(indexName)
    const request = index.getAll(value)

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// Visit note specific operations
export async function savePendingVisitNote(note: PendingVisitNote): Promise<string> {
  // Add to sync queue
  const syncItem: SyncQueueItem = {
    id: `sync_${note.id}`,
    createdAt: Date.now(),
    retryCount: 0,
    storeName: STORES.PENDING_VISIT_NOTES,
    action: 'create',
    itemId: note.id,
  }

  await addItem(STORES.PENDING_VISIT_NOTES, note)
  await addItem(STORES.SYNC_QUEUE, syncItem)

  return note.id
}

export async function getPendingVisitNotes(): Promise<PendingVisitNote[]> {
  return getAllItems<PendingVisitNote>(STORES.PENDING_VISIT_NOTES)
}

export async function removePendingVisitNote(id: string): Promise<void> {
  await deleteItem(STORES.PENDING_VISIT_NOTES, id)
  // Also remove from sync queue
  await deleteItem(STORES.SYNC_QUEUE, `sync_${id}`)
}

// Cache operations for offline viewing
export async function cacheVisit(visit: Record<string, unknown>): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_VISITS, 'readwrite')
    const store = transaction.objectStore(STORES.CACHED_VISITS)
    const request = store.put(visit)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function getCachedVisits(caregiverId: string): Promise<Record<string, unknown>[]> {
  return getItemsByIndex(STORES.CACHED_VISITS, 'caregiverId', caregiverId)
}

export async function cacheClient(client: Record<string, unknown>): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_CLIENTS, 'readwrite')
    const store = transaction.objectStore(STORES.CACHED_CLIENTS)
    const request = store.put(client)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function getCachedClient(clientId: string): Promise<Record<string, unknown> | undefined> {
  return getItem(STORES.CACHED_CLIENTS, clientId)
}

// Check if IndexedDB is available
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch {
    return false
  }
}
